/**
 * ReferenceLibrary — /#/library
 * W66.X17 — Surface website content into platform.
 *
 * Lists all content in /src/content/ as readable reference cards.
 * Clicking a card navigates to /library/:slug for full-document reading.
 *
 * Canon guardrails (CANON_VISUALS_LOCKED.md 2026-05-29):
 *   5-color palette only. No exclamation points. No emojis.
 *   M-PACT hyphenated. Bryan K. Short, Founder and CEO, Mi6 Group.
 *   No "Nine Gates", "Floors", "Navy", "Maybe", "BANT", "MEDDIC".
 *
 * W66.X17 locked: 2026-06-06
 */

import { useLocation } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import {
  REFERENCE_LIBRARY,
  type ContentCategory,
  LINKEDIN_ARTICLES,
  LINKEDIN_SECTION_LABELS,
  type LinkedInSection,
} from "@/content/index";

const BLACK = "#0B0B0F";
const RED = "#DC2626";

const CATEGORY_LABELS: Record<ContentCategory, string> = {
  methodology: "Methodology",
  "cp-model": "Checkpoint Model",
  manifesto: "Manifesto",
  storyboard: "Storyboard",
};

const CATEGORY_COLOR: Record<ContentCategory, string> = {
  methodology: "#DC2626",
  "cp-model": "#EA580C",
  manifesto: "#6B7280",
  storyboard: "#16A34A",
};

interface CardProps {
  id: string;
  slug: string;
  title: string;
  category: ContentCategory;
  description: string;
  onOpen: (slug: string) => void;
}

function ContentCard({ slug, title, category, description, onOpen }: CardProps) {
  return (
    <article
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E5E7",
        borderRadius: 10,
        padding: "20px 22px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        cursor: "pointer",
        transition: "border-color 0.15s, box-shadow 0.15s",
      }}
      onClick={() => onOpen(slug)}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.borderColor = "#DC2626";
        el.style.boxShadow = "0 2px 12px rgba(220, 38, 38, 0.08)";
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.borderColor = "#E5E5E7";
        el.style.boxShadow = "none";
      }}
    >
      <span
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: CATEGORY_COLOR[category],
        }}
      >
        {CATEGORY_LABELS[category]}
      </span>

      <h3
        style={{
          fontFamily: "Georgia, 'Times New Roman', serif",
          fontSize: 17,
          fontWeight: 700,
          color: BLACK,
          margin: 0,
          lineHeight: 1.25,
        }}
      >
        {title}
      </h3>

      <p
        style={{
          fontSize: 13,
          color: "#525258",
          lineHeight: 1.55,
          margin: 0,
          flex: 1,
        }}
      >
        {description}
      </p>

      <div style={{ marginTop: 8 }}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpen(slug); }}
          style={{
            display: "inline-flex",
            alignItems: "center",
            padding: "6px 14px",
            borderRadius: 6,
            background: BLACK,
            color: "#FFFFFF",
            fontSize: 12,
            fontWeight: 600,
            border: "none",
            cursor: "pointer",
          }}
        >
          Read
        </button>
      </div>
    </article>
  );
}

export default function ReferenceLibrary() {
  const [, setLocation] = useLocation();

  function openSlug(slug: string) {
    setLocation(`/library/${slug}`);
  }

  const byCategory = (cat: ContentCategory) =>
    REFERENCE_LIBRARY.filter((item) => item.category === cat);

  const sections: { cat: ContentCategory; label: string }[] = [
    { cat: "methodology", label: "Methodology and Doctrine" },
    { cat: "cp-model", label: "Checkpoint Models" },
    { cat: "manifesto", label: "Manifestos" },
    { cat: "storyboard", label: "Storyboards" },
  ];

  return (
    <OperatorShell
      breadcrumbs={[{ label: "Home", href: "/" }, { label: "Reference Library" }]}
      pageTitle="Reference Library"
    >
      <header style={{ marginBottom: 40, maxWidth: 680 }}>
        <span
          style={{
            color: RED,
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
          }}
        >
          Reference Library
        </span>
        <h1
          style={{
            fontFamily: "Georgia, 'Times New Roman', serif",
            color: BLACK,
            fontSize: 30,
            fontWeight: 700,
            lineHeight: 1.15,
            margin: "10px 0 10px",
          }}
        >
          Reference Library
        </h1>
        <p style={{ color: "#525258", fontSize: 15, lineHeight: 1.6, margin: 0 }}>
          The body of Mi6 Group doctrine, methodology, and Checkpoint models — available
          inside the platform so that what you read here and what drives the system are
          the same voice.
        </p>
      </header>

      {sections.map(({ cat, label }) => {
        const items = byCategory(cat);
        if (!items.length) return null;
        return (
          <section key={cat} style={{ marginBottom: 48 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "#6B6B70",
                marginBottom: 16,
                paddingBottom: 8,
                borderBottom: "1px solid #E5E5E7",
              }}
            >
              {label}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: 16,
              }}
            >
              {items.map((item) => (
                <ContentCard
                  key={item.id}
                  {...item}
                  onOpen={openSlug}
                />
              ))}
            </div>
          </section>
        );
      })}

      {/* ── LinkedIn Articles by Bryan ─────────────────────────────────── */}
      <LinkedInArticlesBlock onOpen={(slug) => setLocation(`/library/linkedin/${slug}`)} />

      <p style={{ marginTop: 20, fontSize: 12, color: "#6B6B70" }}>
        Mi6 Group · Attilius · Reference Library · June 2026
      </p>
    </OperatorShell>
  );
}

// ─── LinkedIn Articles section ────────────────────────────────────────────

const LINKEDIN_SECTION_ORDER: LinkedInSection[] = [
  "pipeline-2025",
  "operations-2026",
  "strategy-2025",
  "foundations-2015-2017",
];

function LinkedInArticlesBlock({ onOpen }: { onOpen: (slug: string) => void }) {
  return (
    <section style={{ marginTop: 64, marginBottom: 48 }}>
      {/* Header band */}
      <div
        style={{
          marginBottom: 32,
          paddingBottom: 16,
          borderBottom: `2px solid ${BLACK}`,
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: RED,
            marginBottom: 8,
          }}
        >
          From the Founder
        </div>
        <h2
          style={{
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontSize: 24,
            fontWeight: 700,
            color: BLACK,
            margin: "0 0 8px",
            lineHeight: 1.2,
          }}
        >
          LinkedIn Articles by Bryan K. Short
        </h2>
        <p style={{ fontSize: 14, color: "#525258", lineHeight: 1.6, margin: 0, maxWidth: 680 }}>
          The original writing that became the Mi6 Group methodology. The first form of the
          M-PACT doctrine, plus operating thinking on continuous improvement, strategy, and
          the discipline of running a sales organization.
        </p>
      </div>

      {LINKEDIN_SECTION_ORDER.map((sectionKey) => {
        const articles = LINKEDIN_ARTICLES
          .filter((a) => a.section === sectionKey)
          .sort((a, b) => a.order - b.order);
        if (!articles.length) return null;
        return (
          <div key={sectionKey} style={{ marginBottom: 36 }}>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "#6B6B70",
                marginBottom: 14,
                paddingBottom: 8,
                borderBottom: "1px solid #E5E5E7",
              }}
            >
              {LINKEDIN_SECTION_LABELS[sectionKey]}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: 16,
              }}
            >
              {articles.map((a) => (
                <LinkedInCard
                  key={a.slug}
                  slug={a.slug}
                  title={a.title}
                  dateDisplay={a.dateDisplay}
                  thumbnail={a.thumbnail}
                  onOpen={onOpen}
                />
              ))}
            </div>
          </div>
        );
      })}
    </section>
  );
}

function LinkedInCard({
  slug,
  title,
  dateDisplay,
  thumbnail,
  onOpen,
}: {
  slug: string;
  title: string;
  dateDisplay: string;
  thumbnail: string;
  onOpen: (slug: string) => void;
}) {
  return (
    <article
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E5E7",
        borderRadius: 10,
        padding: 0,
        display: "flex",
        flexDirection: "column",
        gap: 0,
        cursor: "pointer",
        transition: "border-color 0.15s, box-shadow 0.15s",
        overflow: "hidden",
      }}
      onClick={() => onOpen(slug)}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.borderColor = "#DC2626";
        el.style.boxShadow = "0 2px 12px rgba(220, 38, 38, 0.08)";
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLElement;
        el.style.borderColor = "#E5E5E7";
        el.style.boxShadow = "none";
      }}
    >
      {/* Thumbnail — 16:9, full card width */}
      <div style={{ position: "relative", width: "100%", paddingTop: "56.25%" }}>
        <img
          src={thumbnail}
          alt=""
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      </div>

      {/* Card body */}
      <div style={{ padding: "16px 20px 18px", display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "#6B7280",
          }}
        >
          {dateDisplay}
        </div>
        <h3
          style={{
            fontFamily: "Georgia, 'Times New Roman', serif",
            fontSize: 16,
            fontWeight: 700,
            color: BLACK,
            margin: 0,
            lineHeight: 1.3,
            flex: 1,
          }}
        >
          {title}
        </h3>
        <div style={{ marginTop: 8 }}>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpen(slug); }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "5px 12px",
              borderRadius: 6,
              background: BLACK,
              color: "#FFFFFF",
              fontSize: 11,
              fontWeight: 600,
              border: "none",
              cursor: "pointer",
            }}
          >
            Read
          </button>
        </div>
      </div>
    </article>
  );
}
